import { useEffect, useRef } from "react";
import { X, ScrollText, AlertTriangle } from "lucide-react";
import { TERMS_VERSION, TERMS_EFFECTIVE_DATE, TERMS_SECTIONS } from "@/saas/legal/terms";

/**
 * Kullanım Şartları & İptal Politikası modalı.
 *
 * Props:
 *   open      : boolean
 *   onClose   : () => void
 *   onAccept  : () => void
 *   showAccept: boolean (default true)
 *   forced    : boolean (default false) — Phase 8.2 gating: ESC/backdrop/Kapat etkisiz
 *   version   : string (opsiyonel; default TERMS_VERSION)
 */
export default function TermsModal({
  open, onClose, onAccept,
  showAccept = true, forced = false, version = TERMS_VERSION,
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !forced) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, forced]);

  if (!open) return null;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget && !forced) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-sm"
      onClick={handleBackdrop}
      data-testid="terms-modal"
      data-forced={forced ? "1" : "0"}
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-2xl max-h-[88vh] flex flex-col bg-surface border border-white/10 rounded-2xl shadow-[0_30px_120px_-20px_rgba(245,166,35,0.25)] overflow-hidden"
      >
        {forced && (
          <div className="px-6 py-3 bg-amber-500/15 border-b border-amber-500/30 flex items-center gap-2 text-xs text-amber-200" data-testid="terms-modal-forced-banner">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span>
              Sözleşmemiz güncellendi (sürüm <strong className="font-mono">{version}</strong>).
              Hesabınıza erişmek için yeni şartları onaylamanız gerekiyor.
            </span>
          </div>
        )}

        <header className="flex items-start justify-between gap-4 p-6 border-b border-white/10 shrink-0">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gold/15 border border-gold/30 grid place-items-center shrink-0">
              <ScrollText className="w-5 h-5 text-gold" />
            </div>
            <div>
              <h2
                id="terms-modal-title"
                className="font-display font-extrabold text-2xl tracking-tight text-white"
              >
                Kullanım Şartları & İptal Politikası
              </h2>
              <p className="text-xs text-white/50 mt-1 font-mono">
                Sürüm <span className="text-gold">{version}</span>
                <span className="mx-2 text-white/20">•</span>
                Yürürlük: {TERMS_EFFECTIVE_DATE}
              </p>
            </div>
          </div>
          {!forced && (
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 grid place-items-center text-white/70 hover:text-white transition-colors shrink-0"
              data-testid="terms-modal-close"
              aria-label="Kapat"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 text-sm leading-relaxed text-white/80">
          {TERMS_SECTIONS.map((s) => (
            <section key={s.id} data-testid={`terms-section-${s.id}`}>
              <h3 className="font-semibold text-white text-base mb-1.5">{s.title}</h3>
              <p>{s.body}</p>
            </section>
          ))}

          <div className="mt-6 p-4 rounded-xl bg-gold/5 border border-gold/20 text-[13px] text-white/75">
            <p>
              "Okudum &amp; Kabul Ediyorum" butonuna basarak yukarıdaki maddeleri okuduğunuzu, içeriğini
              anladığınızı ve <span className="font-semibold text-gold">{version}</span> sürümlü
              sözleşmeyi kabul ettiğinizi beyan etmiş olursunuz. Onayınız kayıt altına alınır
              (tarih ve IP ile).
            </p>
          </div>
        </div>

        <footer className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-t border-white/10 bg-bg/60 shrink-0">
          <p className="text-[11px] font-mono uppercase tracking-widest text-white/40">
            Sürüm {version}
          </p>
          <div className="flex items-center gap-2 justify-end">
            {!forced && (
              <button
                type="button"
                onClick={onClose}
                className="h-10 px-5 rounded-md border border-white/15 bg-transparent text-white/80 hover:bg-white/5 hover:border-white/30 text-sm font-semibold transition-colors"
                data-testid="terms-modal-cancel"
              >
                Kapat
              </button>
            )}
            {showAccept && (
              <button
                type="button"
                onClick={() => { onAccept?.(); if (!forced) onClose?.(); }}
                className="h-10 px-5 rounded-md bg-gold text-black hover:bg-gold-hover text-sm font-bold tracking-tight transition-colors active:translate-y-[1px]"
                data-testid="terms-modal-accept"
              >
                Okudum &amp; Kabul Ediyorum
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
